import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'

import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useMemo } from 'modules/core/hooks/useMemo'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import { actions } from 'modules/automations/store/collections/actions'
import { automations } from 'modules/automations/store/collections/automations'
import type { Action } from 'modules/automations/store/models/Action'
import type { Automation } from 'modules/automations/store/models/Automation'
import type {
  ITriggerCondition,
  ITriggerWithConditions,
} from 'modules/automations/store/models/Trigger'

import List from './List'
import { SelectedAction } from './types'

export interface Props {
  automationId: number
  parentActionId: number
  trigger: ITriggerWithConditions
  onClose: () => void
}

function isTriggerAction(item: SelectedAction): item is ITriggerCondition {
  return !item.hasOwnProperty('isAction')
}

const AddAutomationBlockModal = ({ onClose, trigger, automationId, parentActionId }: Props) => {
  const [selectedAction, setAction] = React.useState<SelectedAction | null | undefined>(null)

  const parentAction = useMemo<Action | null | undefined>(
    () =>
      actions.find({
        id: parentActionId,
      }),
    [parentActionId]
  )

  const automation = useMemo<Automation>(() => {
    const automation = automations.find({
      id: automationId,
    })

    if (!automation) {
      throw new Error(`Missing automation with id: ${automationId}`)
    }

    return automation
  }, [automationId])

  const extensionFeatureHasConditions = () => {
    return trigger.conditions?.length !== 0
  }

  const onSubmit = async (closePortal: ClosePortalFn) => {
    if (!selectedAction) return

    const parentId = parentAction?.attributes.id

    const { success } = await asyncAction(async () => {
      let actionParams = {
        automations_automation_id: automation.attributes.id,
        automations_action_id: parentId,
        actionable_type: '',
        metadata: {},
      }

      if (!isTriggerAction(selectedAction)) {
        actionParams = {
          ...actionParams,
          actionable_type: selectedAction.actionableType,
          metadata: selectedAction.metadata ?? {},
        }
      }

      // TODO: Review it with actions in next phase
      if (extensionFeatureHasConditions() && isTriggerAction(selectedAction)) {
        actionParams = {
          ...actionParams,
          actionable_type: selectedAction.actionable_type,
          metadata: {
            operations: selectedAction.operations.map((item) => {
              return {
                ...item,
                value: null,
              }
            }),
          },
        }
      }

      await actions.create(actionParams)

      return actions.fetch()
    })

    if (success) closePortal()
  }

  const setSelected = (action: SelectedAction) => () => {
    setAction(action)
  }

  return (
    <Modal
      illustration="calendar"
      title={i18n.t('automations.add_block.title')}
      description={i18n.t('automations.add_block.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <Button disabled={!selectedAction} submit>
          {i18n.t('automations.add_block.submit')}
        </Button>
      }
    >
      <List
        automation={automation}
        trigger={trigger}
        selected={selectedAction}
        setSelected={setSelected}
      />
    </Modal>
  )
}

export default AddAutomationBlockModal
